import { useState, useEffect } from 'react';
import { List, X, Globe } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLang } from '@/context/LanguageContext';
import { UI_TEXT } from '@/data/tradeData';

const LOGO_URL = "/logo.svg";

export default function Navbar({ minimal }) {
  const { lang, setLang } = useLang();
  const t = UI_TEXT[lang].nav;
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = minimal ? [] : [
    { label: t.services, href: '#services' },
    { label: t.about, href: '#about' },
    { label: t.portfolio, href: '#portfolio' },
    { label: t.testimonials, href: '#testimonials' },
  ];

  const toggleLanguage = () => {
    const newLang = lang === 'en' ? 'es' : 'en';
    setLang(newLang);
    const path = location.pathname;
    if (newLang === 'es' && !path.startsWith('/es')) {
      const map = { '/': '/es', '/roofing': '/es/techos', '/impact-windows': '/es/ventanas-de-impacto', '/hvac': '/es/aire-acondicionado', '/remodeling': '/es/remodelacion', '/electrical': '/es/electrico', '/privacy': '/es/privacidad', '/terms': '/es/terminos', '/sms-terms': '/es/terminos-sms' };
      navigate(map[path] || '/es');
    } else if (newLang === 'en' && path.startsWith('/es')) {
      const map = { '/es': '/', '/es/techos': '/roofing', '/es/ventanas-de-impacto': '/impact-windows', '/es/aire-acondicionado': '/hvac', '/es/remodelacion': '/remodeling', '/es/electrico': '/electrical', '/es/privacidad': '/privacy', '/es/terminos': '/terms', '/es/terminos-sms': '/sms-terms' };
      navigate(map[path] || '/');
    }
  };

  return (
    <nav data-testid="navbar" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'backdrop-blur-xl bg-black/80 border-b border-white/10' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-40 pt-2">
        <a href={lang === 'es' ? '/es' : '/'} data-testid="navbar-logo" className="flex items-center">
          <img src={LOGO_URL} alt="CSFL" className="h-40 w-auto object-contain" />
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map(link => (
            <a key={link.label} href={link.href} className="text-sm font-black text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)] hover:text-[#E31937] transition-colors tracking-wide uppercase">{link.label}</a>
          ))}
          <button onClick={toggleLanguage} data-testid="lang-toggle" className="flex items-center gap-1.5 text-sm font-black text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)] hover:text-[#E31937] transition-colors uppercase">
            <Globe size={16} /> {lang === 'en' ? 'ES' : 'EN'}
          </button>
          <a href="tel:3059048567" data-testid="nav-cta" className="bg-[#E31937] text-white font-bold text-sm uppercase tracking-wider px-5 py-2.5 hover:bg-[#FF2445] transition-colors">
            {t.getQuote}
          </a>
        </div>

        <button data-testid="mobile-menu-toggle" className="lg:hidden text-white p-2" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <X size={24} weight="bold" /> : <List size={24} weight="bold" />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="lg:hidden bg-black/95 backdrop-blur-xl border-b border-white/10 overflow-hidden">
            <div className="px-6 py-6 flex flex-col gap-4">
              {navLinks.map(link => (
                <a key={link.label} href={link.href} className="text-base font-bold text-[#A3A3A3] hover:text-white uppercase tracking-wide" onClick={() => setMobileOpen(false)}>{link.label}</a>
              ))}
              <button onClick={() => { toggleLanguage(); setMobileOpen(false); }} className="text-base font-bold text-[#A3A3A3] hover:text-white uppercase tracking-wide text-left flex items-center gap-2">
                <Globe size={18} /> {lang === 'en' ? 'Español' : 'English'}
              </button>
              <a href="tel:3059048567" className="bg-[#E31937] text-white font-bold text-center uppercase tracking-wider px-6 py-3 hover:bg-[#FF2445] transition-colors mt-2">305-904-8567</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
